import { stringify } from "query-string";
import jwt from "jwt-simple";
import axios from "axios";
import config from "../config/config";
import paramsToFormData from "../utils/params-to-form-data";
import {
  GET_LIST,
  GET_ONE,
  GET_MANY,
  GET_MANY_REFERENCE,
  UPDATE,
  CREATE,
  DELETE,
  HttpError,
} from "react-admin";

export const MEDIA_UPLOAD_PROGRESS_EVENT = "media-upload-progress";

const apiUrl = config.api.url;
const apiKey = config.api.api_key;
const jwtKey = config.jwt.key;
const localStorageAuth = config.local_storage.auth;
const localStorageUser = config.local_storage.user;

const authToken = () => {
  try {
    return JSON.parse(
      jwt.decode(
        localStorage.getItem(jwt.encode(localStorageAuth, jwtKey)),
        jwtKey
      )
    ).auth_token;
  } catch (err) {
    return "";
  }
};

const permissions = () => {
  const userkey = localStorage.getItem(jwt.encode(localStorageUser, jwtKey));
  if (userkey !== null && userkey !== undefined) {
    const { roles } = JSON.parse(jwt.decode(userkey, jwtKey));
    return roles ? roles : "admin-customer";
  }
};

export const headers = () => {
  const token = authToken();
  return {
    Accept: "application/json",
    "Content-Type": "multipart/form-data",
    "x-api-key": apiKey,
    "x-token": "Bearer " + token,
  };
};

const normalizeResourceName = (resource) => {
  if (resource === "days_group") return "days-group";
  if (resource === "type_spot") return "type-spot";
  if (resource === "type_scheme") return "type-scheme";
  if (resource === "type_plan") return "type-plan";
  if (resource === "category_media") return "category-media";
  if (resource === "media_download") return "media-download";
  if (resource === "media_download_map") return "media-download-map";
  if (resource === "spot_day") return "spot-day";
  if (resource === "spot_file") return "spot-file";
  if (resource === "spot_download") return "spot-download";
  if (resource === "spot_receipt") return "spot-receipt";
  if (resource === "spot_download_map") return "spot-download-map";
  if (resource === "customer_programming_map") return "customer-programming-map";
  if (resource === "customer_media") return "customer-media";
  if (resource === "customer_spot_receipt") return "customer-spot-receipt";
  if (resource === "customer_notification") return "customer-notification";
  if (resource === "customer_staff_member") return "customer-staff-member";
  if (resource === "customer_transmitter") return "customer-transmitter";
  if (resource === "customer_note") return "customer-note";
  if (resource === "affiliation_request") return "affiliation-request";
  if (resource === "map_layer") return "map-layer";
  return resource;
};

const toHttpError = (error) => {
  const status = error?.response?.status || 500;
  const payload = error?.response?.data;
  if (payload && payload.error) return new HttpError(payload.error, status);
  return new HttpError(error?.response?.statusText || error.message || "Request Error", status);
};

const mapProviderResponse = (type, response) => {
  switch (type) {
    case GET_LIST:
    case GET_MANY: {
      const data = response.data;
      const result = response.result;
      const meta = response.meta || {};

      return {
        data: data ? data : result,
        total: meta.total || 1,
        pagination: {
          page: meta.page || 1,
          perPage: meta.perpage || 10,
        },
      };
    }
    case GET_MANY_REFERENCE:
      return {
        data: response.data,
        total: response.data.length,
      };
    case GET_ONE:
      return { data: response.result };
    case UPDATE:
    case CREATE:
      return { data: response.result };
    default:
      return { data: [] };
  }
};

const getBrowserFile = (item) => {
  if (!item) return null;
  if (Array.isArray(item) && item.length > 0) return getBrowserFile(item[0]);
  if (typeof File !== "undefined" && item instanceof File) return item;
  if (item.rawFile && typeof File !== "undefined" && item.rawFile instanceof File) return item.rawFile;
  return null;
};

const readImage = (fileOrBlob) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(fileOrBlob);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(objectUrl);
      reject(err);
    };
    img.src = objectUrl;
  });

const blobToFile = (blob, name) => {
  if (typeof File !== "undefined") {
    return new File([blob], name, { type: blob.type || "application/octet-stream" });
  }
  blob.name = name;
  return blob;
};

const normalizeThumbnailImage = async (fileOrBlob, originalName = "thumbnail.jpg") => {
  const thumbnailCfg = config?.media_thumbnail || {};
  const maxWidth = Number(thumbnailCfg.width || 320);
  const maxHeight = Number(thumbnailCfg.height || 180);
  const quality = Number(thumbnailCfg.quality || 0.85);
  const mimeType = String(thumbnailCfg.mime_type || "image/jpeg");
  const safeName = `${(originalName || "thumbnail").replace(/\.[^/.]+$/, "")}.${mimeType.includes("png") ? "png" : mimeType.includes("webp") ? "webp" : "jpg"}`;

  const image = await readImage(fileOrBlob);
  const widthRatio = maxWidth / image.width;
  const heightRatio = maxHeight / image.height;
  const scale = Math.min(widthRatio, heightRatio, 1);
  const targetWidth = Math.max(1, Math.round(image.width * scale));
  const targetHeight = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

  const blob = await new Promise((resolve, reject) =>
    canvas.toBlob((out) => (out ? resolve(out) : reject(new Error("thumbnail conversion failed"))), mimeType, quality)
  );

  return blobToFile(blob, safeName);
};

const captureVideoFrameThumbnail = async (videoFile) => {
  const thumbnailCfg = config?.media_thumbnail || {};
  const captureSecond = Number(thumbnailCfg.frame_capture_second || 1);
  const video = document.createElement("video");
  video.crossOrigin = "anonymous";
  video.preload = "metadata";
  video.muted = true;
  video.playsInline = true;

  const objectUrl = URL.createObjectURL(videoFile);

  try {
    await new Promise((resolve, reject) => {
      video.onloadedmetadata = resolve;
      video.onerror = reject;
      video.src = objectUrl;
    });

    const seekSecond = Number.isFinite(video.duration)
      ? Math.min(Math.max(0, captureSecond), Math.max(0, video.duration - 0.05))
      : 0;

    await new Promise((resolve, reject) => {
      video.onseeked = resolve;
      video.onerror = reject;
      video.currentTime = seekSecond;
    });

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, video.videoWidth || 1);
    canvas.height = Math.max(1, video.videoHeight || 1);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const rawFrameBlob = await new Promise((resolve, reject) =>
      canvas.toBlob((out) => (out ? resolve(out) : reject(new Error("video frame capture failed"))), "image/jpeg", 0.92)
    );

    return normalizeThumbnailImage(rawFrameBlob, `${videoFile.name || "video"}-frame.jpg`);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const uploadThumbnailWithPresignedUrl = async ({ categoryMediaId, thumbnailFile }) => {
  if (!thumbnailFile || !categoryMediaId) return null;

  const presignedResp = await axios({
    headers: headers(),
    method: "POST",
    url: `${apiUrl}/media/presigned-thumbnail-upload`,
    data: paramsToFormData({
      category_media_id: categoryMediaId,
      thumbnail_file_json: JSON.stringify({
        name: thumbnailFile.name || "thumbnail.jpg",
        type: thumbnailFile.type || "application/octet-stream",
        size: thumbnailFile.size || 0,
      }),
    }),
  });

  const thumbnailUpload = presignedResp?.data?.result?.thumbnail_upload || null;
  if (!thumbnailUpload) return null;

  await axios.put(thumbnailUpload.signed_url, thumbnailFile, {
    headers:
      thumbnailUpload.headers || {
        "Content-Type": thumbnailFile.type || "application/octet-stream",
      },
  });

  return {
    key: thumbnailUpload.key,
    url: thumbnailUpload.url,
    type: thumbnailUpload.type,
  };
};

const provider = async (type, resource, params) => {
  let normalizedResource = normalizeResourceName(resource);
  let url = "";

  if (
    params != null &&
    params.data != null &&
    params.data.days_broadcast != null
  ) {
    params.data.days_broadcast = params.data.days_broadcast[0].split(",");
  }

  const publishMediaUploadProgress = (payload) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent(MEDIA_UPLOAD_PROGRESS_EVENT, { detail: payload })
    );
  };

  const isMediaUploadMutation =
    normalizedResource === "media" && (type === CREATE || type === UPDATE);

  const options = {
    headers: headers(),
    data: paramsToFormData(params?.data),
  };

  switch (type) {
    case GET_LIST: {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const query = {
        sort: JSON.stringify([field, order]),
        range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
        filter: JSON.stringify(params.filter),
      };

      if (normalizedResource === "customer-media")
        url = `${apiUrl}/customer-media/medias?${stringify(query)}`;
      else if (normalizedResource === "customer-category-media")
        url = `${apiUrl}/customer-media/categories?${stringify(query)}`;
      else if (normalizedResource === "customer-spot-receipt-spots")
        url = `${apiUrl}/customer-spot-receipt/spots?${stringify(query)}`;
      else url = `${apiUrl}/${normalizedResource}?${stringify(query)}`;

      options.url = url;
      break;
    }
    case GET_ONE: {
      if (normalizedResource === "dashboard-embed-url")
        url = `${apiUrl}/dashboard/${params.id}/embed-url`;
      else if (normalizedResource === "settings") url = `${apiUrl}/settings`;
      else if (normalizedResource === "customer-media")
        url = `${apiUrl}/customer-media/${params.id}/media`;
      else url = `${apiUrl}/${normalizedResource}/${params.id}`;

      options.url = url;
      break;
    }
    case GET_MANY: {
      if (normalizedResource === "customer-media")
        url = `${apiUrl}/customer-media/medias`;
      else if (normalizedResource === "customer-category-media")
        url = `${apiUrl}/customer-media/categories`;
      else if (normalizedResource === "customer-spot-receipt-spots")
        url = `${apiUrl}/customer-spot-receipt/spots`;
      else url = `${apiUrl}/${normalizedResource}`;

      options.url = url;
      break;
    }
    case GET_MANY_REFERENCE: {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const query = {
        sort: JSON.stringify([field, order]),
        range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
        filter: JSON.stringify({
          ...params.filter,
          [params.target]: params.id,
        }),
      };

      url = `${apiUrl}/${normalizedResource}?${stringify(query)}`;
      options.url = url;
      break;
    }
    case UPDATE:
      url = `${apiUrl}/${normalizedResource}/${params.id}`;
      options.method = "PUT";
      options.url = url;
      break;
    case CREATE:
      url = `${apiUrl}/${normalizedResource}`;
      options.method = "POST";
      options.url = url;
      break;
    case DELETE:
      url = `${apiUrl}/${normalizedResource}/${params.id}`;
      options.method = "DELETE";
      options.url = url;
      break;
    default:
      throw new Error(`Unsupported Data Provider request type ${type}`);
  }

  try {
    // Fluxo media com upload direto no S3 (pré-assinado)
    if (
      normalizedResource === "media" &&
      (type === CREATE || type === UPDATE)
    ) {
      const data = { ...(params?.data || {}) };
      const mediaClass = data.media_class;
      const isFileMedia = mediaClass === "Audio" || mediaClass === "Video";
      const isTextMedia = mediaClass === "Texto";

      publishMediaUploadProgress({ active: true, percent: 0 });

      if (type === CREATE && isFileMedia) {
        const files = Array.isArray(data.files)
          ? data.files.map(getBrowserFile).filter(Boolean)
          : [];
        const thumbnailInputFile = getBrowserFile(data.thumbnail_file);
        const shouldCaptureVideoFrame =
          mediaClass === "Video" && !thumbnailInputFile && Boolean(data.thumbnail_from_video_frame);

        let thumbnailFile = null;
        if (thumbnailInputFile) {
          thumbnailFile = await normalizeThumbnailImage(thumbnailInputFile, thumbnailInputFile.name || "thumbnail.jpg");
        } else if (shouldCaptureVideoFrame && files.length === 1) {
          thumbnailFile = await captureVideoFrameThumbnail(files[0]);
        } else if (shouldCaptureVideoFrame && files.length > 1) {
          throw new Error("A captura de frame para thumbnail requer apenas 1 vídeo por envio.");
        }

        if (files.length > 0) {
          const filesMeta = files.map((file) => ({
            name: file.name,
            type: file.type || "application/octet-stream",
            size: file.size || 0,
          }));

          const presignedResp = await axios({
            headers: headers(),
            method: "POST",
            url: `${apiUrl}/media/presigned-uploads`,
            data: paramsToFormData({
              category_media_id: data.category_media_id,
              media_class: mediaClass,
              files_json: JSON.stringify(filesMeta),
            }),
          });

          const uploads = presignedResp?.data?.result?.uploads || [];
          const totalBytes = files.reduce((acc, file) => acc + (file.size || 0), 0) || 1;
          let uploadedBytes = 0;

          for (let index = 0; index < files.length; index++) {
            const file = files[index];
            const upload = uploads[index];
            if (!upload) continue;
            const baseUploadedBytes = uploadedBytes;

            await axios.put(upload.signed_url, file, {
              headers: upload.headers || { "Content-Type": file.type || "application/octet-stream" },
              onUploadProgress: (event) => {
                const loaded = event?.loaded || 0;
                const percent = Math.min(
                  99,
                  Math.round(((baseUploadedBytes + loaded) * 100) / totalBytes)
                );
                publishMediaUploadProgress({ active: true, percent });
              },
            });

            uploadedBytes += file.size || 0;
          }

          const uploadedThumbnail = await uploadThumbnailWithPresignedUrl({
            categoryMediaId: data.category_media_id,
            thumbnailFile,
          });

          delete data.files;
          delete data.thumbnail_file;
          delete data.thumbnail_from_video_frame;
          data.uploads_json = JSON.stringify(uploads.map((item) => ({
            key: item.key,
            url: item.url,
            type: item.type,
            name: item.name,
            original_name: item.original_name,
          })));
          if (uploadedThumbnail) {
            data.thumbnail_upload_json = JSON.stringify(uploadedThumbnail);
          }
        }
      }

      if (type === CREATE && isTextMedia) {
        const thumbnailInputFile = getBrowserFile(data.thumbnail_file);
        if (thumbnailInputFile) {
          const thumbnailFile = await normalizeThumbnailImage(
            thumbnailInputFile,
            thumbnailInputFile.name || "thumbnail.jpg"
          );

          const uploadedThumbnail = await uploadThumbnailWithPresignedUrl({
            categoryMediaId: data.category_media_id,
            thumbnailFile,
          });
          if (uploadedThumbnail)
            data.thumbnail_upload_json = JSON.stringify(uploadedThumbnail);
        }
        delete data.thumbnail_file;
      }

      if (type === UPDATE && isFileMedia) {
        const selectedFile = getBrowserFile(data.file);
        const thumbnailInputFile = getBrowserFile(data.thumbnail_file);
        const shouldCaptureVideoFrame =
          mediaClass === "Video" && !thumbnailInputFile && Boolean(data.thumbnail_from_video_frame) && selectedFile;
        let thumbnailFile = null;
        if (thumbnailInputFile) {
          thumbnailFile = await normalizeThumbnailImage(thumbnailInputFile, thumbnailInputFile.name || "thumbnail.jpg");
        } else if (shouldCaptureVideoFrame) {
          thumbnailFile = await captureVideoFrameThumbnail(selectedFile);
        }

        if (selectedFile) {
          const categoryId =
            data.category_media_id ||
            params?.previousData?.category_media_id ||
            params?.previousData?.category_media?.id;

          const filesMeta = [
            {
              name: selectedFile.name,
              type: selectedFile.type || "application/octet-stream",
              size: selectedFile.size || 0,
            },
          ];

          const presignedResp = await axios({
            headers: headers(),
            method: "POST",
            url: `${apiUrl}/media/presigned-uploads`,
            data: paramsToFormData({
              category_media_id: categoryId,
              media_class: mediaClass,
              files_json: JSON.stringify(filesMeta),
            }),
          });

          const upload = presignedResp?.data?.result?.uploads?.[0];
          if (upload) {
            const totalBytes = selectedFile.size || 1;
            await axios.put(upload.signed_url, selectedFile, {
              headers: upload.headers || { "Content-Type": selectedFile.type || "application/octet-stream" },
              onUploadProgress: (event) => {
                const loaded = event?.loaded || 0;
                const percent = Math.min(
                  99,
                  Math.round((loaded * 100) / totalBytes)
                );
                publishMediaUploadProgress({ active: true, percent });
              },
            });

            data.uploaded_file_json = JSON.stringify({
              key: upload.key,
              url: upload.url,
              type: upload.type,
            });
          }
        }

        const categoryId =
          data.category_media_id ||
          params?.previousData?.category_media_id ||
          params?.previousData?.category_media?.id;
        const uploadedThumbnail = await uploadThumbnailWithPresignedUrl({
          categoryMediaId: categoryId,
          thumbnailFile,
        });
        if (uploadedThumbnail)
          data.thumbnail_uploaded_file_json = JSON.stringify(uploadedThumbnail);

        delete data.file;
        delete data.thumbnail_file;
        delete data.thumbnail_from_video_frame;
      }

      if (type === UPDATE && isTextMedia) {
        const thumbnailInputFile = getBrowserFile(data.thumbnail_file);
        if (thumbnailInputFile) {
          const thumbnailFile = await normalizeThumbnailImage(
            thumbnailInputFile,
            thumbnailInputFile.name || "thumbnail.jpg"
          );
          const categoryId =
            data.category_media_id ||
            params?.previousData?.category_media_id ||
            params?.previousData?.category_media?.id;

          const uploadedThumbnail = await uploadThumbnailWithPresignedUrl({
            categoryMediaId: categoryId,
            thumbnailFile,
          });
          if (uploadedThumbnail)
            data.thumbnail_uploaded_file_json = JSON.stringify(uploadedThumbnail);
        }
        delete data.thumbnail_file;
      }

      if (Array.isArray(data.descriptions)) {
        data.descriptions = JSON.stringify(data.descriptions);
      }

      options.data = paramsToFormData(data);
      const response = (await axios(options)).data;
      publishMediaUploadProgress({ active: false, percent: 100 });
      return mapProviderResponse(type, response);
    }

    if (isMediaUploadMutation) {
      publishMediaUploadProgress({ active: true, percent: 0 });
      options.onUploadProgress = (progressEvent) => {
        const total = progressEvent.total || 0;
        if (!total) return;
        const percent = Math.round((progressEvent.loaded * 100) / total);
        publishMediaUploadProgress({ active: true, percent });
      };
    }

    const response = (await axios(options)).data;
    if (isMediaUploadMutation) {
      publishMediaUploadProgress({ active: false, percent: 100 });
    }
    return mapProviderResponse(type, response);
  } catch (error) {
    if (isMediaUploadMutation) {
      publishMediaUploadProgress({ active: false, percent: 0 });
    }
    console.log(error);
    return Promise.reject(toHttpError(error));
  }
};

const fetchURLFile = (id, model, params) => {
  const options = {
    headers: headers(),
    url: `${apiUrl}/${model}/${id}/download`,
    data: paramsToFormData(params),
    method: "POST",
  };

  return axios(options).then((res) => {
    return res.data;
  });
};

const unreadNotification = () => {
  // CUSTOMER
  if (permissions() === "admin-customer") {
    const options = {
      headers: headers(),
      url: `${apiUrl}/customer-notification`,
      params: { filter: '{"unread": true}', range: "[0, 1000]" },
      method: "GET",
    };

    return axios(options).then((res) => {
      return res.data;
    });
  } else return [];
};

const readNotification = (id) => {
  const options = {
    headers: headers(),
    url: `${apiUrl}/customer-notification/${id}`,
    method: "PUT",
  };

  return axios(options).then((res) => {
    return res.data;
  });
};

export { fetchURLFile, unreadNotification, readNotification };
export default provider;
